/* Regroupement des dossiers de contenu par classe, partagé entre l'écran
   bibliothèque et la fiche classe. Pure : aucun rendu, aucun appel réseau —
   `folders.list` renvoie déjà `classroom_id`, rien à demander au serveur. */

export interface ClassroomFolder {
  id: string;
  name: string;
  classroom_id: string | null;
  document_count: number;
}

export interface ClassroomRef {
  id: string;
  label: string;
}

export interface ClassroomFolderGroup {
  classroomId: string | null;
  label: string;
  folders: ClassroomFolder[];
  documentCount: number;
}

export const UNASSIGNED_GROUP_LABEL = 'Sans classe';

function byName(a: ClassroomFolder, b: ClassroomFolder): number {
  return a.name.localeCompare(b.name, 'fr');
}

function sumDocuments(folders: ClassroomFolder[]): number {
  return folders.reduce((total, folder) => total + folder.document_count, 0);
}

/*
 * Un groupe par classe fournie, dans l'ordre des classes reçu — y compris les
 * classes sans dossier, qui restent visibles : c'est le cas courant tant qu'un
 * enseignant n'a rattaché aucun dossier, et les masquer ferait disparaître la
 * classe de son propre écran de rangement.
 *
 * Les dossiers dont la classe est absente de `classrooms` (classe d'un autre
 * enseignant, classe supprimée — `classroom_id` retombe à NULL en base via
 * ON DELETE SET NULL) tombent dans le groupe sans classe plutôt que d'être
 * perdus.
 */
export function groupFoldersByClassroom(
  folders: readonly ClassroomFolder[],
  classrooms: readonly ClassroomRef[],
): ClassroomFolderGroup[] {
  const known = new Set(classrooms.map((c) => c.id));

  const groups = classrooms.map((classroom) => {
    const owned = folders.filter((f) => f.classroom_id === classroom.id).sort(byName);
    return {
      classroomId: classroom.id,
      label: classroom.label,
      folders: owned,
      documentCount: sumDocuments(owned),
    };
  });

  const orphans = folders
    .filter((f) => f.classroom_id === null || !known.has(f.classroom_id))
    .sort(byName);

  return [
    ...groups,
    {
      classroomId: null,
      label: UNASSIGNED_GROUP_LABEL,
      folders: orphans,
      documentCount: sumDocuments(orphans),
    },
  ];
}

/* Plafond de dossiers montrés d'emblée dans une section : un enseignant en aura
   dix à quinze par classe, et au-delà de deux rangées pleines une seule classe
   occupe tout l'écran. Partagé pour que la fiche classe ne diverge pas. */
export const FOLDERS_BEFORE_CLIP = 6;

export interface ClippedFolders {
  shown: ClassroomFolder[];
  overflow: number;
}

export function sectionKey(classroomId: string | null): string {
  return classroomId ?? 'sans-classe';
}

/*
 * `overflow` compte les dossiers au-dela du plafond independamment de `expanded` :
 * c'est lui qui dit s'il faut proposer de deplier, y compris une fois deplie pour
 * proposer de replier.
 */
export function clipFolders(
  folders: readonly ClassroomFolder[],
  expanded: boolean,
  limit: number = FOLDERS_BEFORE_CLIP,
): ClippedFolders {
  const overflow = Math.max(0, folders.length - limit);
  return {
    shown: expanded || overflow === 0 ? [...folders] : folders.slice(0, limit),
    overflow,
  };
}

/*
 * Résumé porté par l'en-tête d'une section, seul contenu visible quand elle est
 * repliée : il doit donc annoncer les dossiers, pas seulement les documents.
 */
export function countLabel(folderCount: number, documentCount: number): string {
  if (folderCount === 0) return 'aucun dossier';
  const folders = `${folderCount} ${folderCount > 1 ? 'dossiers' : 'dossier'}`;
  if (documentCount === 0) return `${folders} · vide`;
  return `${folders} · ${documentCount} ${documentCount > 1 ? 'documents' : 'document'}`;
}
